import React, { createContext, useContext, useEffect, useState } from 'react';
import Sentry from '../sentry';
import { createDbApi, type DbApi } from './api';

// Define the context type with API access
export interface DbContextType {
  isLoading: boolean;
  error: Error | null;
  retry: () => Promise<void>;
  api: DbApi;
}

// Create the context with undefined default value
export const dbContext = createContext<DbContextType | undefined>(undefined);

// Provider component for the DB context
export const DbProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Create a stable reference to the API with useState
  const [api] = useState(() => createDbApi());

  const initialize = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await api.init();
    } catch (err) {
      console.error('Failed to initialize IndexedDB:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize on component mount
  useEffect(() => {
    initialize();
  }, []);

  return (
    <dbContext.Provider value={{
      isLoading,
      error,
      retry: initialize,
      api
    }}>
      {children}
    </dbContext.Provider>
  );
};

// Hook for mandatory DB context access (throws if used outside Provider)
export const useDbContext = (): DbContextType => {
  const context = useContext(dbContext);
  if (context === undefined) {
    throw new Error('useDbContext must be used within a DbProvider');
  }
  return context;
};

// Hook for optional DB context access (returns undefined if used outside Provider)
export const useDbContextMaybe = () => {
  return useContext(dbContext);
};
